/** @format */

import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  faDroplet,
  faLocationDot,
  faPhone,
  faPlug,
  faSnowflake,
  faStore,
  faSun,
  faVideo,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  AlertTitle,
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { fr } from 'date-fns/locale';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api.ts';
import { parseFieldError } from '../../../utils/errors';
import BoxLoader from '../../misc/BoxLoader';
import CustomAutocomplete from '../../misc/CustomAutocomplete';
import MultiDropzone from '../../misc/MultiDropzone';

function defaultMeetingDurationMinutes(meetingFormat) {
  if (meetingFormat === 'telephone') return 30;
  if (meetingFormat === 'physique') return 90;
  return 60;
}

const PROJECT_TYPE_OPTIONS = [
  { value: 'pv', label: 'PV', icon: faSun },
  { value: 'aa', label: 'PAC A/A', icon: faSnowflake },
  { value: 'ev', label: 'Borne VE', icon: faPlug },
  { value: 'ae', label: 'PAC A/E', icon: faDroplet },
];

const PROJECT_TYPE_VALUES = PROJECT_TYPE_OPTIONS.map((option) => option.value);

function parseProjectTypes(raw) {
  if (Array.isArray(raw)) {
    return raw.filter((value) => PROJECT_TYPE_VALUES.includes(value));
  }
  if (PROJECT_TYPE_VALUES.includes(raw)) {
    return [raw];
  }
  return [];
}

function FollowUpDialog({
  lead,
  open,
  onClose,
  action,
  onChange,
  section,
  modifyExisting = false,
  preferredCalendarSiteId,
}) {
  const firstLeadSite =
    lead && lead.sites && lead.sites.length === 1 ? lead.sites[0] : null;

  const [loading, setLoading] = useState(false);
  const [to, setTo] = useState(lead.email);
  const [pmMail, setPmMail] = useState('');
  const [adminMail, setAdminMail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [isSMS, setIsSMS] = useState(false);
  const [isEmail, setIsEmail] = useState(false);

  const [selectedSite, setSelectedSite] = useState(firstLeadSite);
  const [calendarSelectedSite, setCalendarSelectedSite] = useState(
    lead?.sites?.[0] ?? null,
  );
  const [calendarSites, setCalendarSites] = useState([]);
  const [meetingFormat, setMeetingFormat] = useState('');
  const [projectTypes, setProjectTypes] = useState([]);
  const [durationMinutes, setDurationMinutes] = useState(null);
  const [meetingNotesDeRdv, setMeetingNotesDeRdv] = useState('');
  const [meetingName, setMeetingName] = useState('');
  const [sendAutoRemindersToLead, setSendAutoRemindersToLead] = useState(true);
  const [activeProjectManagers, setActiveProjectManagers] = useState([]);
  const [calendarInvitePm, setCalendarInvitePm] = useState(null);
  const [selectedJobType, setSelectedJobType] = useState('');
  const [actionComment, setActionComment] = useState('');
  const [actionFiles, setActionFiles] = useState([]);
  const [editingJob, setEditingJob] = useState(null);
  const [jobTypes, setJobTypes] = useState([]);
  const [isExtraMessage, setIsExtraMessage] = useState(false);
  const [pendingExtraMessage, setPendingExtraMessage] = useState(false);
  const [extraMessageTemplate, setExtraMessageTemplate] = useState(null);

  const { enqueueSnackbar } = useSnackbar();

  const refreshDefaultMeetingName = useCallback(
    (pmUser) => {
      if (!lead?.id || !pmUser?.id) return;
      api
        .get(`/follow-up/${lead.id}/default_meeting_name/`, {
          calendar_invited_project_manager: pmUser.id,
        })
        .then((res) => setMeetingName(res.data?.default_meeting_name || ''))
        .catch(() => {});
    },
    [lead?.id],
  );

  const projectManagerInviteChoices = useMemo(() => {
    const byId = new Map(activeProjectManagers.map((u) => [u.id, u]));
    if (calendarInvitePm?.id != null && !byId.has(calendarInvitePm.id)) {
      return [...activeProjectManagers, calendarInvitePm];
    }
    return activeProjectManagers;
  }, [activeProjectManagers, calendarInvitePm]);

  const loadTemplateMessage = (specificMessageTemplate = null) => {
    setLoading(true);
    let specificMessageType = '';
    if (specificMessageTemplate) {
      if (specificMessageTemplate.startsWith('FOLLOW_UP_S')) {
        specificMessageType = 'sms';
      } else {
        specificMessageType = 'email';
      }
    }
    const data = specificMessageTemplate
      ? { template: specificMessageTemplate, message_type: specificMessageType }
      : {};
    api
      .post(`follow-up/${lead.id}/get_follow_up_message_content/`, {}, data)
      .then((result) => {
        setSubject(result.data.subject);
        setBody(result.data.body);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        const { message } = parseFieldError(err);
        if (message) enqueueSnackbar(message, { variant: 'error' });
      });
  };

  const loadMails = () => {
    setLoading(true);
    api
      .get('/user-configs/')
      .then((result) => {
        if (result.data.results[0]) {
          setAdminMail(result.data.results[0].admin_user.email);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        const { state, message } = parseFieldError(err);
        if (message) enqueueSnackbar(message, { variant: 'error' });
        setFieldErrors(state || {});
        setLoading(false);
      });
  };

  const messageMapping = {
    is_contact: {
      1: 'FOLLOW_UP_M1_1',
      2: 'FOLLOW_UP_S1_1',
      3: 'FOLLOW_UP_S1_2',
      5: 'FOLLOW_UP_M1_2',
    },
    is_follow_up_r1: {
      1: 'FOLLOW_UP_M2_1',
      2: 'FOLLOW_UP_S2_1',
      3: 'FOLLOW_UP_S2_2',
      5: 'FOLLOW_UP_M2_2',
    },
    is_follow_up_r2: {
      1: 'FOLLOW_UP_M3_1',
      2: 'FOLLOW_UP_S3_1',
      3: 'FOLLOW_UP_S3_2',
      5: 'FOLLOW_UP_M3_2',
    },
    is_follow_up_r3: {
      1: 'FOLLOW_UP_M4_1',
      2: 'FOLLOW_UP_S4_1',
      3: 'FOLLOW_UP_S4_2',
      5: 'FOLLOW_UP_M4_2',
    },
  };

  function setMessageType() {
    let status = null;
    if (
      lead.current_follow_up_status &&
      lead.current_follow_up_status.is_contact
    ) {
      status = 'is_contact';
    } else if (
      lead.current_follow_up_status &&
      (lead.current_follow_up_status.is_follow_up_r1 ||
        lead.current_follow_up_status.is_confirmation_r1)
    ) {
      status = 'is_follow_up_r1';
    } else if (
      lead.current_follow_up_status &&
      (lead.current_follow_up_status.is_follow_up_r2 ||
        lead.current_follow_up_status.is_confirmation_r2)
    ) {
      status = 'is_follow_up_r2';
    } else if (
      lead.current_follow_up_status &&
      (lead.current_follow_up_status.is_follow_up_r3 ||
        lead.current_follow_up_status.is_confirmation_r3)
    ) {
      status = 'is_follow_up_r3';
    }

    const callNumber = lead.follow_up_call.call_number;

    if (isExtraMessage && lead?.current_follow_up_status?.is_contact) {
      setIsSMS(true);
      setIsEmail(false);
      setExtraMessageTemplate('FOLLOW_UP_S1_0');
      // loadTemplateMessage(extraMessageTemplate);
      return;
    }

    if (messageMapping[status] && messageMapping[status][callNumber]) {
      const messageType = messageMapping[status][callNumber];
      if (messageType.startsWith('FOLLOW_UP_S')) {
        setIsSMS(true);
        setIsEmail(false);
      } else if (messageType.startsWith('FOLLOW_UP_M')) {
        setIsEmail(true);
        setIsSMS(false);
      }
    }
  }

  const handleOpenEmail = () => {
    setTo(lead.email);
    setFieldErrors({});
    loadMails();
  };

  const handleOpenSMS = () => {
    setTo(lead.phone_1);
    setFieldErrors({});
  };

  const handleClose = () => {
    if (isExtraMessage) {
      setIsExtraMessage(false);
      onClose();
    } else {
      onClose();
      setTimeout(() => {
        setMeetingDate('');
        setMeetingTime('');
        setCalendarSelectedSite(
          (() => {
            const list = lead?.sites;
            if (!list?.length) return null;
            if (preferredCalendarSiteId != null) {
              const match = list.find((s) => s.id === preferredCalendarSiteId);
              if (match) return match;
            }
            return list[0];
          })(),
        );
        setCalendarSites([]);
        setMeetingFormat('');
        setProjectTypes([]);
        setDurationMinutes(null);
        setMeetingNotesDeRdv('');
        setMeetingName('');
        setSendAutoRemindersToLead(true);
        setActiveProjectManagers([]);
        setCalendarInvitePm(null);
        setTo(lead.email);
        setSubject('');
        setBody('');
        setFieldErrors({});
        setActionComment('');
        setSelectedJobType('');
        setActionFiles([]);
        setEditingJob(null);
        setFieldErrors({});
      }, 500);
    }
  };

  const updateMeeting = () => {
    setLoading(true);
    const meetingDateValue =
      meetingDate && meetingTime
        ? (() => {
            const pad = (n) => String(n).padStart(2, '0');
            const dateStr = `${meetingDate.getFullYear()}-${pad(
              meetingDate.getMonth() + 1,
            )}-${pad(meetingDate.getDate())}`;
            const timeStr = meetingTime.toTimeString().slice(0, 5);
            return moment(`${dateStr}T${timeStr}:00`).toDate();
          })()
        : null;
    const data = {
      meeting_date: meetingDateValue,
      ...(calendarSelectedSite?.id != null && {
        site: calendarSelectedSite.id,
      }),
      ...(meetingFormat && { meeting_format: meetingFormat }),
      ...(projectTypes.length > 0 && { project_type: projectTypes }),
      ...(durationMinutes != null && { duration_minutes: durationMinutes }),
      meeting_notes: meetingNotesDeRdv || '',
      ...(meetingName && { meeting_name: meetingName }),
      send_auto_reminders_to_lead: sendAutoRemindersToLead,
      ...(calendarInvitePm?.id != null && {
        calendar_invited_project_manager: calendarInvitePm.id,
      }),
    };
    api
      .patch(`/follow-up/${lead.id}/update_meeting/`, {}, data)
      .then(() => {
        setLoading(false);
        handleClose();
        if (onChange) onChange();
        enqueueSnackbar('Le RDV a été modifié.', { variant: 'success' });
      })
      .catch((err) => {
        const { state, message } = parseFieldError(err);
        enqueueSnackbar(message ?? 'Impossible de modifier le RDV.', {
          variant: 'error',
        });
        setFieldErrors(state || {});
        setLoading(false);
      });
  };

  const cancelMeeting = () => {
    setLoading(true);
    api
      .post(
        `/follow-up/${lead.id}/call_follow_up/`,
        {},
        {
          lead: lead.id,
          result: 'not_scheduled',
          call_date: new Date().toISOString(),
          action: 'communication',
        },
      )
      .then(() => {
        setLoading(false);
        handleClose();
        if (onChange) onChange();
        enqueueSnackbar('Le RDV a été annulé.', { variant: 'success' });
      })
      .catch((err) => {
        const { message } = parseFieldError(err);
        enqueueSnackbar(message ?? "Impossible d'annuler le RDV.", {
          variant: 'error',
        });
        setLoading(false);
      });
  };

  const updateFollowUpData = (result, isSetR2 = false) => {
    setLoading(true);
    let shouldSetR2 = isSetR2;
    if (lead.current_follow_up_status?.is_confirmation_r2) {
      shouldSetR2 = true;
    }
    let site = null;
    if (section === 'calendar') site = calendarSelectedSite?.id;
    else if (section === 'technicalVisitCalendar') site = selectedSite?.id;
    const data = {
      lead: lead.id,
      action: section === 'message' ? 'voicemail' : 'communication',
      result,
      is_set_r2: shouldSetR2,
      call_date: new Date().toISOString(),
      meeting_date:
        meetingDate && meetingTime
          ? (() => {
              const pad = (n) => String(n).padStart(2, '0');
              const dateStr = `${meetingDate.getFullYear()}-${pad(
                meetingDate.getMonth() + 1,
              )}-${pad(meetingDate.getDate())}`;
              const timeStr = meetingTime.toTimeString().slice(0, 5);
              return moment(`${dateStr}T${timeStr}:00`).toDate();
            })()
          : null,
      ...(site != null && { site }),
      ...(section === 'calendar' &&
        meetingFormat && { meeting_format: meetingFormat }),
      ...(section === 'calendar' &&
        projectTypes.length > 0 && { project_type: projectTypes }),
      ...(section === 'calendar' &&
        durationMinutes != null && { duration_minutes: durationMinutes }),
      ...(section === 'calendar' && {
        meeting_notes: meetingNotesDeRdv || '',
      }),
      ...(section === 'calendar' &&
        meetingName && { meeting_name: meetingName }),
      ...(section === 'calendar' && {
        send_auto_reminders_to_lead: sendAutoRemindersToLead,
      }),
      ...(section === 'calendar' &&
        calendarInvitePm?.id != null && {
          calendar_invited_project_manager: calendarInvitePm.id,
        }),
    };
    api
      .post(`/follow-up/${lead.id}/call_follow_up/`, {}, data)
      .then(() => {
        setLoading(false);
        handleClose();
        if (onChange) {
          onChange();
        }
        enqueueSnackbar('Les informations ont été mise à jour', {
          variant: 'success',
        });
      })
      .catch((err) => {
        const { state, message } = parseFieldError(err);
        enqueueSnackbar(
          message ?? 'Impossible de mettre à jour les informations',
          {
            variant: 'error',
          },
        );
        setFieldErrors(state || {});
        setLoading(false);
      });
  };

  const confirmMeeting = (
    isConfirmed,
    hasPlannedNextMeeting,
    hasSigned,
    isSetR2 = false,
  ) => {
    setLoading(true);
    let site = null;
    if (section === 'calendar') site = calendarSelectedSite?.id;
    else if (section === 'technicalVisitCalendar') site = selectedSite?.id;
    const data = {
      lead: lead.id,
      is_confirmed: isConfirmed,
      has_planned_next_meeting: hasPlannedNextMeeting,
      has_signed: hasSigned,
      is_set_r2: isSetR2,
      meeting_date:
        meetingDate && meetingTime
          ? (() => {
              const pad = (n) => String(n).padStart(2, '0');
              const dateStr = `${meetingDate.getFullYear()}-${pad(
                meetingDate.getMonth() + 1,
              )}-${pad(meetingDate.getDate())}`;
              const timeStr = meetingTime.toTimeString().slice(0, 5);
              return moment(`${dateStr}T${timeStr}:00`).toDate();
            })()
          : null,
      ...(site != null && { site }),
      ...(section === 'calendar' &&
        meetingFormat && { meeting_format: meetingFormat }),
      ...(section === 'calendar' &&
        projectTypes.length > 0 && { project_type: projectTypes }),
      ...(section === 'calendar' &&
        durationMinutes != null && { duration_minutes: durationMinutes }),
      ...(section === 'calendar' &&
        meetingName && { meeting_name: meetingName }),
      send_auto_reminders_to_lead: sendAutoRemindersToLead,
      ...(section === 'calendar' &&
        calendarInvitePm?.id != null && {
          calendar_invited_project_manager: calendarInvitePm.id,
        }),
    };

    api
      .post(`/follow-up/${lead.id}/confirm_meeting/`, {}, data)
      .then(() => {
        setLoading(false);
        handleClose();
        if (onChange) {
          onChange();
        }
        enqueueSnackbar('Les informations ont été mises à jour', {
          variant: 'success',
        });
      })
      .catch((err) => {
        const { state, message } = parseFieldError(err);
        enqueueSnackbar(
          message ?? 'Impossible de mettre à jour les informations',
          {
            variant: 'error',
          },
        );
        setFieldErrors(state || {});
        setLoading(false);
      });
  };

  const needsExtraSMS = () =>
    !isExtraMessage &&
    lead.current_follow_up_status.is_contact &&
    lead.follow_up_call.call_number === 1 &&
    isEmail;

  const notSendingFistMail = () => {
    setPendingExtraMessage(true);
    setIsExtraMessage(true);
    enqueueSnackbar('Préparation du SMS...', {
      variant: 'success',
    });
  };

  const sendFollowUpMessage = () => {
    if (loading) return;
    if (isEmail && (!to || !subject || !body)) {
      enqueueSnackbar("Les champs obligatoires n'ont pas été remplis !", {
        variant: 'error',
      });
      return;
    }
    if (isSMS && (!to || !body)) {
      enqueueSnackbar("Les champs obligatoires n'ont pas été remplis !", {
        variant: 'error',
      });
      return;
    }
    setLoading(true);
    let message = null;
    if (isEmail) {
      message = 'mail_sent';
    } else if (isSMS) {
      message = 'text_message_sent';
    }
    let data = {
      to,
      subject,
      body,
      copy: [pmMail, adminMail],
      action: message,
    };
    if (isExtraMessage) {
      data = {
        ...data,
        template: extraMessageTemplate,
        message_type: 'sms',
      };
    }
    api
      .post(`/follow-up/${lead.id}/send_follow_up_message/`, {}, data)
      .then(() => {
        if (needsExtraSMS()) {
          setPendingExtraMessage(true);
          setIsExtraMessage(true);
          setLoading(false);
          enqueueSnackbar("L'e-mail a bien été envoyé. Préparation du SMS...", {
            variant: 'success',
          });
        } else {
          updateFollowUpData('no_response');
          setLoading(false);
          enqueueSnackbar(
            isEmail
              ? "L'e-mail a bien été envoyé."
              : 'Le SMS a bien été envoyé.',
            {
              variant: 'success',
            },
          );
          handleClose();
        }
      })
      .catch((err) => {
        const { state, errorMessage } = parseFieldError(err);
        enqueueSnackbar(errorMessage ?? "Impossible d'envoyer ce message.", {
          variant: 'error',
        });
        setFieldErrors(state || {});
        setLoading(false);
      });
  };

  // TECHNICAL VISIT

  /**
   * Fetch job types
   */
  const updateJobTypes = () => {
    setLoading(true);
    api
      .get('/bigchange-job-types/')
      .then((result) => {
        let localJobTypes = [];
        if (Array.isArray(result.data)) {
          localJobTypes = result.data.filter((jobType) =>
            jobType.label.startsWith('Visite '),
          );
        }
        setSelectedJobType(localJobTypes[2].label);
        setJobTypes(localJobTypes);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        const { message } = parseFieldError(err);
        if (message) enqueueSnackbar(message, { variant: 'error' });
      });
  };

  /**
   * Upload attachment
   * @param type {string} Attachment type
   * @param id {number} Attachment id
   * @param file {File} File to upload
   * @returns {Promise<AxiosResponse<any> | void>|null} API response
   */
  const uploadAttachment = (type, id, file) => {
    const attachmentBody = new FormData();
    attachmentBody.append('type', type);
    attachmentBody.append('id', id);
    attachmentBody.append('file', file);
    return api.post(
      `/leads-sites/${selectedSite.id}/upload_attachments/`,
      {},
      attachmentBody,
      { 'Content-Type': 'multipart/form-data' },
    );
  };

  /**
   * Create technical visit job
   * @param internalType {string} Job internal type, can be 'project_manager' or 'technician'
   * @param comment {string} Job comment
   * @param type {number} Job type id
   * @param date {string} Job date, format DD/MM/YYYY
   * @param hour {string} Job hour, format HH:mm
   * @param quote {number} Job quote
   * @param files {Array} Job files
   * @param projectManagerPresence {boolean} Project manager presence
   * @param priority {boolean} Job priority
   * @param update {boolean} Update job
   */
  const createTechnicalVisit = (
    internalType,
    comment,
    type,
    date,
    hour,
    quote,
    files,
    projectManagerPresence = false,
    priority = false,
    update = false,
  ) => {
    if (!selectedSite) return;
    setFieldErrors({});
    setLoading(true);
    const isProjectManager = internalType === 'project_manager';
    const isTechnician = internalType === 'technician';
    const technicalVisitBody = {
      comment,
      type,
      // date: isProjectManager
      //   ? moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD')
      //   : null,
      date: isProjectManager
        ? moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD')
        : null,
      hour: isProjectManager ? moment(hour, 'HH:mm').format('HH:mm') : null,
      quote: null,
      quote_v2: isTechnician ? quote && quote.id : null,
      project_manager_presence: isTechnician ? projectManagerPresence : null,
      priority: isTechnician ? priority : null,
      internal_type: internalType,
      selected_client: null,
      is_follow_up: true,
    };
    if (update) {
      delete technicalVisitBody.type;
      technicalVisitBody.job_id = lead.follow_up_meeting.bc_job_id;
    }
    const endpoint = update
      ? `/leads-sites/${selectedSite.id}/update_own_technical_visit/`
      : `/leads-sites/${selectedSite.id}/create_technical_visit/`;
    api
      .post(endpoint, {}, technicalVisitBody)
      .then((result) => {
        const promises = files.map((file) =>
          uploadAttachment(
            'job',
            update ? lead.follow_up_meeting.bc_job_id : result.data.jobid,
            file,
          ),
        );
        return Promise.all(promises);
      })
      .then(() => {
        enqueueSnackbar(
          `Une visite technique a bien été ${
            date && hour ? 'programmée' : 'demandée'
          }.`,
          { variant: 'success' },
        );
        handleClose();
        if (onChange) {
          onChange();
        }
      })
      .catch((err) => {
        const { state, message } = parseFieldError(err);
        if (message) enqueueSnackbar(message, { variant: 'error' });
        setLoading(false);
        setFieldErrors(state || {});
      });
  };

  const isMeetingConfirmation =
    lead.current_follow_up_status?.is_confirmation_r1 ||
    lead.current_follow_up_status?.is_confirmation_r2 ||
    lead.current_follow_up_status?.is_confirmation_r3 ||
    lead.current_follow_up_status?.is_r1_completed ||
    lead.current_follow_up_status?.is_confirmation_technical_visit;

  const handleValidation = () => {
    if (section === 'calendar') {
      const missingSite = calendarSites.length > 0 && !calendarSelectedSite;
      const missingFields =
        !meetingDate ||
        !meetingTime ||
        !meetingFormat ||
        projectTypes.length === 0 ||
        !calendarInvitePm;
      if (missingSite || missingFields) {
        enqueueSnackbar("Les champs obligatoires n'ont pas été remplis !", {
          variant: 'error',
        });
        return;
      }
    }
    if (
      section === 'technicalVisitCalendar' &&
      (!selectedSite || !selectedJobType || !meetingDate || !meetingTime)
    ) {
      enqueueSnackbar("Les champs obligatoires n'ont pas été remplis !", {
        variant: 'error',
      });
      return;
    }

    if (section === 'calendar' && modifyExisting) {
      updateMeeting();
      return;
    }

    const firstRequest = new Promise((resolve, reject) => {
      if (isMeetingConfirmation) {
        if (
          (lead.current_follow_up_status.is_confirmation_r1 ||
            lead.current_follow_up_status.is_r1_completed ||
            lead.current_follow_up_status.is_confirmation_r2) &&
          section === 'calendar'
        ) {
          confirmMeeting(true, true, false, true);
        } else {
          confirmMeeting(true, true, false);
        }
        resolve();
      } else {
        if (section === 'calendar') {
          updateFollowUpData('scheduled', true);
        } else {
          updateFollowUpData('scheduled');
        }
        resolve();
      }
    });

    firstRequest
      .then(() => {
        // if (
        //   (lead.follow_up_meeting?.meeting_type === 'r2' &&
        //     !isMeetingConfirmation) ||
        //   (isMeetingConfirmation && lead.follow_up_meeting?.meeting_type === 'r1')
        //
        // ) {
        if (section === 'technicalVisitCalendar') {
          if (!selectedSite) return;
          createTechnicalVisit(
            'project_manager',
            actionComment,
            selectedJobType,
            meetingDate,
            meetingTime,
            null,
            actionFiles,
            false,
            false,
            lead.follow_up_meeting?.bc_job_id !== null,
          );
        }
      })
      .catch((err) => {
        const { state, message } = parseFieldError(err);
        if (message) enqueueSnackbar(message, { variant: 'error' });
        setLoading(false);
        setFieldErrors(state || {});
      });
  };

  useEffect(() => {
    if (open && section === 'message') {
      setMessageType();
      if (isEmail) {
        handleOpenEmail();
      } else if (isSMS) {
        handleOpenSMS();
      }
    }
  }, [open, action, isEmail, isSMS, isExtraMessage]);

  useEffect(() => {
    if (open && section === 'message') {
      if (isExtraMessage && extraMessageTemplate) {
        loadTemplateMessage(extraMessageTemplate);
      } else if (!pendingExtraMessage && !isExtraMessage) {
        loadTemplateMessage();
      }
    }
  }, [
    open,
    section,
    pendingExtraMessage,
    isExtraMessage,
    extraMessageTemplate,
  ]);

  useEffect(() => {
    if (pendingExtraMessage && isExtraMessage && body) {
      setPendingExtraMessage(false);
    }
  }, [body, pendingExtraMessage, isExtraMessage]);

  useEffect(() => {
    if (section === 'technicalVisitCalendar') {
      updateJobTypes();
    }
  }, [section, open]);

  useEffect(() => {
    if (open && section === 'calendar' && lead?.id) {
      api
        .get(`/leads-sites/`, { lead: lead.id, page_size: 20, public: true })
        .then((res) => {
          const sites = res.data?.results ?? [];
          setCalendarSites(sites);
          if (modifyExisting && lead.follow_up_meeting?.site?.id) {
            const match = sites.find(
              (s) => s.id === lead.follow_up_meeting.site.id,
            );
            setCalendarSelectedSite(match ?? sites[0]);
          } else if (sites.length >= 1) {
            const preferred =
              preferredCalendarSiteId != null
                ? sites.find((s) => s.id === preferredCalendarSiteId)
                : null;
            setCalendarSelectedSite(preferred ?? sites[0]);
          }
        })
        .catch(() => setCalendarSites([]));
    }
  }, [
    open,
    section,
    lead?.id,
    modifyExisting,
    lead?.follow_up_meeting?.site?.id,
    preferredCalendarSiteId,
  ]);

  useEffect(() => {
    if (
      open &&
      section === 'calendar' &&
      modifyExisting &&
      lead?.follow_up_meeting?.scheduled_date
    ) {
      const m = moment(lead.follow_up_meeting.scheduled_date);
      if (m.isValid()) {
        setMeetingDate(m.toDate());
        setMeetingTime(m.toDate());
      }
      if (
        lead.follow_up_meeting?.meeting_format &&
        ['telephone', 'visio', 'physique', 'showroom'].includes(
          lead.follow_up_meeting.meeting_format,
        )
      ) {
        setMeetingFormat(lead.follow_up_meeting.meeting_format);
      }
      setProjectTypes(parseProjectTypes(lead.follow_up_meeting?.project_type));
      if (lead.follow_up_meeting?.duration_minutes != null) {
        setDurationMinutes(lead.follow_up_meeting.duration_minutes);
      } else if (
        lead.follow_up_meeting?.meeting_format &&
        ['telephone', 'visio', 'physique', 'showroom'].includes(
          lead.follow_up_meeting.meeting_format,
        )
      ) {
        setDurationMinutes(
          defaultMeetingDurationMinutes(lead.follow_up_meeting.meeting_format),
        );
      }
      if (lead.follow_up_meeting?.meeting_notes != null) {
        setMeetingNotesDeRdv(lead.follow_up_meeting.meeting_notes);
      }
      if (lead.follow_up_meeting?.meeting_name != null) {
        setMeetingName(lead.follow_up_meeting.meeting_name);
      }
      setSendAutoRemindersToLead(
        lead.follow_up_meeting?.send_auto_reminders_to_lead !== false,
      );
    } else if (open && section === 'calendar' && lead) {
      setSendAutoRemindersToLead(true);
      setProjectTypes([]);
    }
  }, [open, section, modifyExisting, lead]);

  useEffect(() => {
    if (!open || section !== 'calendar' || !lead?.id) {
      setActiveProjectManagers([]);
      setCalendarInvitePm(null);
      return undefined;
    }

    const resolvePmId = (pm) => {
      if (pm == null || pm === '') return null;
      if (typeof pm === 'object' && pm.id != null) return pm.id;
      const n = Number(pm);
      return Number.isFinite(n) ? n : null;
    };

    const storedId = modifyExisting
      ? lead.follow_up_meeting?.calendar_invited_project_manager
      : null;
    const defaultPmId = resolvePmId(lead.project_manager);
    const targetId = storedId ?? defaultPmId;
    if (targetId != null) {
      const pmRef = lead.project_manager;
      const immediate =
        typeof pmRef === 'object' && pmRef != null && pmRef.id === targetId
          ? pmRef
          : { id: targetId };
      setCalendarInvitePm(immediate);
    } else {
      setCalendarInvitePm(null);
    }

    let cancelled = false;
    api
      .get('/users/', { is_project_manager: 'True', page_size: 500 })
      .then((res) => {
        if (cancelled) return;
        const managers = res.data?.results ?? [];
        setActiveProjectManagers(managers);
        const fromStored =
          storedId != null && managers.find((u) => u.id === storedId);
        const fromDefaultList =
          defaultPmId != null && managers.find((u) => u.id === defaultPmId);
        const selected =
          fromStored ||
          fromDefaultList ||
          (typeof lead.project_manager === 'object' &&
          lead.project_manager != null
            ? lead.project_manager
            : null) ||
          (targetId != null ? { id: targetId } : null);
        setCalendarInvitePm(selected);
        if (!modifyExisting && selected) {
          refreshDefaultMeetingName(selected);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setActiveProjectManagers([]);
        let fallback = null;
        if (
          typeof lead.project_manager === 'object' &&
          lead.project_manager != null
        ) {
          fallback = lead.project_manager;
        } else if (targetId != null) {
          fallback = { id: targetId };
        }
        setCalendarInvitePm(fallback);
        if (!modifyExisting && fallback) {
          refreshDefaultMeetingName(fallback);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    section,
    lead?.id,
    modifyExisting,
    lead?.follow_up_meeting?.calendar_invited_project_manager,
    lead?.project_manager?.id,
    refreshDefaultMeetingName,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      scroll="paper"
      fullWidth
    >
      <DialogTitle
        sx={{
          backgroundColor: '#408B6A66',
          color: '#000',
          padding: '1em',
          borderRadius: '4px',
          textAlign: 'center',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {section === 'calendar' && modifyExisting
          ? 'Modification du RDV'
          : 'Prise de RDV'}
      </DialogTitle>

      <DialogContent dividers>
        <BoxLoader loading={loading} whiteBackground>
          <Grid container justifyContent="center" spacing={1.5}>
            {section === 'calendar' && (
              <>
                <Grid item xs={12}>
                  <CustomAutocomplete
                    name="site"
                    label="Choix du site concerné"
                    value={calendarSelectedSite}
                    required
                    disabled={loading || calendarSites.length === 1}
                    autoLoad
                    endpoint={(input) =>
                      `/leads-sites/?search=${
                        input === 0 || input === undefined ? '' : input
                      }&lead=${lead.id}&page_size=20&public=True`
                    }
                    getOptionLabel={(o) =>
                      o === '' ? null : `${o.address_1} ${o.zip_code} ${o.city}`
                    }
                    onChange={(e, v) => setCalendarSelectedSite(v)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={fr}
                      >
                        <DatePicker
                          label="Date du RDV"
                          value={meetingDate || null}
                          required
                          allowSameDateSelection
                          onChange={setMeetingDate}
                          slotProps={{
                            textField: {
                              variant: 'outlined',
                              fullWidth: true,
                              size: 'small',
                              error: Boolean(fieldErrors.dateError),
                              helperText: fieldErrors.dateError,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={6}>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={fr}
                      >
                        <TimePicker
                          label="Heure du RDV"
                          value={meetingTime || null}
                          required
                          ampm={false}
                          onChange={setMeetingTime}
                          minTime={moment('07:00', 'HH:mm')}
                          maxTime={moment('20:00', 'HH:mm')}
                          slotProps={{
                            textField: {
                              variant: 'outlined',
                              fullWidth: true,
                              size: 'small',
                              error: Boolean(fieldErrors.hourError),
                              helperText: fieldErrors.hourError,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <TextField
                      select
                      label="Chargé de projet"
                      value={calendarInvitePm?.id ?? ''}
                      onChange={(e) => {
                        const id = Number(e.target.value);
                        const u = projectManagerInviteChoices.find(
                          (x) => x.id === id,
                        );
                        if (u) {
                          setCalendarInvitePm(u);
                          refreshDefaultMeetingName(u);
                        }
                      }}
                      fullWidth
                      size="small"
                      required
                      disabled={
                        loading || projectManagerInviteChoices.length === 0
                      }
                    >
                      {projectManagerInviteChoices.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.full_name ||
                            `${u.first_name || ''} ${u.last_name || ''}`.trim()}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ mb: 0.5, fontWeight: 600 }}
                  >
                    Type de projet
                  </Typography>
                  <Box
                    display="flex"
                    width="100%"
                    justifyContent="space-around"
                    gap={1}
                  >
                    {PROJECT_TYPE_OPTIONS.map(({ value, label, icon }) => {
                      const selected = projectTypes.includes(value);
                      return (
                        <Box
                          key={value}
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          sx={{ cursor: 'pointer' }}
                          onClick={() =>
                            setProjectTypes((prev) =>
                              prev.includes(value)
                                ? prev.filter((item) => item !== value)
                                : [...prev, value],
                            )
                          }
                        >
                          <IconButton
                            color={selected ? 'primary' : 'default'}
                            size="medium"
                            sx={{
                              minWidth: 48,
                              minHeight: 48,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              ...(selected && {
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                borderColor: 'primary.main',
                                '&:hover': {
                                  bgcolor: 'primary.dark',
                                  color: 'primary.contrastText',
                                  borderColor: 'primary.dark',
                                },
                              }),
                            }}
                          >
                            <FontAwesomeIcon icon={icon} />
                          </IconButton>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ mb: 0.5, fontWeight: 600 }}
                  >
                    Type de RDV
                  </Typography>
                  <Box
                    display="flex"
                    width="100%"
                    justifyContent="space-around"
                    gap={1}
                  >
                    {[
                      {
                        value: 'telephone',
                        label: 'Téléphone',
                        shortLabel: 'Téléphone',
                        icon: faPhone,
                      },
                      {
                        value: 'visio',
                        label: 'Visio',
                        shortLabel: 'Visio',
                        icon: faVideo,
                      },
                      {
                        value: 'physique',
                        label: 'Physique',
                        shortLabel: 'Physique',
                        icon: faLocationDot,
                      },
                      {
                        value: 'showroom',
                        label: 'Showroom',
                        shortLabel: 'Showroom',
                        icon: faStore,
                      },
                    ].map(({ value, label, shortLabel, icon }) => (
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          const next = meetingFormat === value ? '' : value;
                          setMeetingFormat(next);
                          let defaultDuration = null;
                          if (next)
                            defaultDuration =
                              defaultMeetingDurationMinutes(next);
                          setDurationMinutes(defaultDuration);
                        }}
                      >
                        <IconButton
                          color={
                            meetingFormat === value ? 'primary' : 'default'
                          }
                          size="medium"
                          sx={{
                            minWidth: 48,
                            minHeight: 48,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            ...(meetingFormat === value && {
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              borderColor: 'primary.main',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                                color: 'primary.contrastText',
                                borderColor: 'primary.dark',
                              },
                            }),
                          }}
                        >
                          <FontAwesomeIcon icon={icon} />
                        </IconButton>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {shortLabel}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Alert severity="info" sx={{ mt: 2, py: 1 }}>
                    Le lead et le chargé de projet sélectionné seront invités à
                    un événement Google Agenda
                    {meetingFormat === 'visio' &&
                      ", et le lien de la visio sera dans l'événement."}
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sendAutoRemindersToLead}
                        onChange={(e) =>
                          setSendAutoRemindersToLead(e.target.checked)
                        }
                        disabled={loading}
                        color="primary"
                      />
                    }
                    label="Relances automatiques (e-mail la veille, SMS le jour J)"
                  />
                </Grid>
                {meetingFormat && (
                  <Grid item xs={12}>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        {[15, 30, 45, 60, 90, 120].map((mins) => (
                          <Button
                            key={mins}
                            variant={
                              durationMinutes === mins
                                ? 'contained'
                                : 'outlined'
                            }
                            size="small"
                            onClick={() => setDurationMinutes(mins)}
                          >
                            {mins} mins
                          </Button>
                        ))}
                      </Box>
                      <TextField
                        label="Durée (minutes)"
                        type="number"
                        value={durationMinutes ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') {
                            setDurationMinutes(null);
                          } else {
                            const n = parseInt(v, 10);
                            if (!Number.isNaN(n)) {
                              setDurationMinutes(Math.max(1, Math.min(480, n)));
                            }
                          }
                        }}
                        inputProps={{ min: 1, max: 480 }}
                        fullWidth
                        size="small"
                      />
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    label="Notes de RDV"
                    fullWidth
                    multiline
                    rows={2}
                    value={meetingNotesDeRdv}
                    onChange={(e) => setMeetingNotesDeRdv(e.target.value)}
                    disabled={loading}
                    size="small"
                    placeholder="Optionnel — ajoutées aux commentaires du lead"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Nom du RDV"
                    fullWidth
                    value={meetingName}
                    onChange={(e) => setMeetingName(e.target.value)}
                    disabled={loading}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              </>
            )}
            {section === 'technicalVisitCalendar' && (
              <>
                <Grid item xs={12}>
                  <Alert severity="warning">
                    <AlertTitle>
                      <strong>Attention</strong>
                    </AlertTitle>
                    Votre lead va recevoir un e-mail de confirmation, il est
                    important qu&apos;il clique sur le lien à l&apos;intérieur
                    pour confirmer sa présence.
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <CustomAutocomplete
                    name="site"
                    label="Choix du site concerné"
                    value={selectedSite}
                    required
                    endpoint={(input) =>
                      `/leads-sites/?search=${
                        input === 0 || input === undefined ? '' : input
                      }&lead=${lead.id}&page_size=20&public=True`
                    }
                    getOptionLabel={(o) =>
                      o === '' ? null : `${o.address_1} ${o.zip_code} ${o.city}`
                    }
                    onChange={(e, v) => setSelectedSite(v)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper elevation={0}>
                    <TextField
                      select
                      label="Type de mission"
                      fullWidth
                      required
                      id="job-type"
                      value={selectedJobType}
                      error={Boolean(fieldErrors.typeError)}
                      helperText={fieldErrors.typeError}
                      onChange={(e) => setSelectedJobType(e.target.value)}
                      size="small"
                      disabled={Boolean(editingJob)}
                    >
                      {jobTypes.map((jobType) => (
                        <MenuItem value={jobType.label} key={jobType.id}>
                          {jobType.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper elevation={0}>
                    <TextField
                      label="Description"
                      fullWidth
                      multiline
                      rows={4}
                      value={actionComment}
                      error={Boolean(fieldErrors.commentError)}
                      helperText={fieldErrors.commentError}
                      onChange={(e) => setActionComment(e.target.value)}
                      size="small"
                      inputProps={{
                        maxLength: 1000,
                      }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={fr}
                      >
                        <DatePicker
                          label="Date du RDV"
                          value={meetingDate || null}
                          allowSameDateSelection
                          required
                          onChange={setMeetingDate}
                          slotProps={{
                            textField: {
                              variant: 'outlined',
                              fullWidth: true,
                              size: 'small',
                              error: Boolean(fieldErrors.dateError),
                              helperText: fieldErrors.dateError,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={6}>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={fr}
                      >
                        <TimePicker
                          label="Heure du RDV"
                          value={meetingTime || null}
                          required
                          ampm={false}
                          onChange={setMeetingTime}
                          minTime={moment('07:00', 'HH:mm')}
                          maxTime={moment('20:00', 'HH:mm')}
                          slotProps={{
                            textField: {
                              variant: 'outlined',
                              fullWidth: true,
                              size: 'small',
                              error: Boolean(fieldErrors.hourError),
                              helperText: fieldErrors.hourError,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Paper elevation={0}>
                    <MultiDropzone
                      type="file"
                      onFileChange={setActionFiles}
                      selectedFiles={actionFiles}
                    />
                  </Paper>
                </Grid>
              </>
            )}

            {section === 'message' && (
              <>
                {isSMS && (
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      required={isSMS}
                      fullWidth
                      id="to"
                      label="Destinataire"
                      name="to"
                      value={to}
                      onChange={(event) => setTo(event.target.value)}
                      error={Boolean(fieldErrors.toError)}
                      helperText={fieldErrors.toError}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  {isEmail && (
                    <>
                      <Grid container justifyContent="flex-end" spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            variant="outlined"
                            required={isEmail}
                            fullWidth
                            id="to"
                            type="email"
                            label="Destinataire"
                            name="to"
                            value={to}
                            onChange={(event) => setTo(event.target.value)}
                            error={Boolean(fieldErrors.toError)}
                            helperText={fieldErrors.toError}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            id="pmMail"
                            type="email"
                            label="Chargé de projet (copie cachée)"
                            name="pmMail"
                            value={pmMail}
                            onChange={(event) => setPmMail(event.target.value)}
                            error={Boolean(fieldErrors.pmMailError)}
                            helperText={fieldErrors.pmMailError}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            id="adminMail"
                            type="email"
                            label="Administration (copie cachée)"
                            name="adminMail"
                            value={adminMail}
                            onChange={(event) =>
                              setAdminMail(event.target.value)
                            }
                            error={Boolean(fieldErrors.adminMailError)}
                            helperText={fieldErrors.adminMailError}
                          />
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <TextField
                          variant="outlined"
                          required={isEmail}
                          fullWidth
                          id="subject"
                          label="Sujet"
                          name="subject"
                          value={subject}
                          onChange={(event) => setSubject(event.target.value)}
                          error={Boolean(fieldErrors.subjectError)}
                          helperText={fieldErrors.subjectError}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={body}
                    onChange={(event, editor) => setBody(editor.getData())}
                    error={Boolean(fieldErrors.bodyError)}
                    helperText={fieldErrors.bodyError}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </BoxLoader>
      </DialogContent>

      <DialogActions>
        <Grid container justifyContent="space-between" spacing={2}>
          <Grid item>
            <Button
              fullWidth
              variant="contained"
              onClick={handleClose}
              color="inherit"
              disabled={loading}
            >
              Annuler
            </Button>
          </Grid>
          {section === 'calendar' && modifyExisting && (
            <Grid item>
              <Button
                fullWidth
                variant="contained"
                color="error"
                disabled={loading}
                sx={{
                  fontSize: '0.9rem',
                  height: '36px',
                }}
                onClick={cancelMeeting}
              >
                Annuler le RDV
              </Button>
            </Grid>
          )}
          <Grid item>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              disabled={
                loading ||
                (section === 'calendar' &&
                  (!calendarSelectedSite ||
                    !meetingDate ||
                    !meetingTime ||
                    !meetingFormat ||
                    projectTypes.length === 0 ||
                    !calendarInvitePm ||
                    !meetingName))
              }
              sx={{
                fontSize: '1rem',
                fontWeight: 'bold',
                height: '36px',
              }}
              onClick={
                section === 'message' ? sendFollowUpMessage : handleValidation
              }
            >
              {section === 'message' ? 'Envoyer' : 'Valider'}
            </Button>
          </Grid>
          {section === 'message' && (
            <Grid item xs={3.2}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                disabled={loading}
                sx={{
                  fontSize: '0.8rem',
                  height: '36px',
                }}
                onClick={
                  needsExtraSMS()
                    ? notSendingFistMail
                    : () => updateFollowUpData('no_response')
                }
              >
                Ne pas envoyer
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

FollowUpDialog.propTypes = {
  lead: PropTypes.shape({
    sites: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    id: PropTypes.number.isRequired,
    email: PropTypes.string.isRequired,
    phone_1: PropTypes.string.isRequired,
    current_follow_up_status: PropTypes.shape({
      name: PropTypes.string.isRequired,
      is_contact: PropTypes.bool.isRequired,
      is_follow_up_r1: PropTypes.bool.isRequired,
      is_follow_up_r2: PropTypes.bool.isRequired,
      is_follow_up_r3: PropTypes.bool.isRequired,
      is_confirmation_r1: PropTypes.bool.isRequired,
      is_confirmation_r2: PropTypes.bool.isRequired,
      is_confirmation_r3: PropTypes.bool.isRequired,
      is_confirmation_technical_visit: PropTypes.bool.isRequired,
      is_r1_completed: PropTypes.bool.isRequired,
    }).isRequired,
    follow_up_meeting: PropTypes.shape({
      meeting_type: PropTypes.string.isRequired,
      meeting_number: PropTypes.number.isRequired,
      bc_job_id: PropTypes.number,
      scheduled_date: PropTypes.string,
      meeting_format: PropTypes.string,
      project_type: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      duration_minutes: PropTypes.number,
      meeting_notes: PropTypes.string,
      meeting_name: PropTypes.string,
      send_auto_reminders_to_lead: PropTypes.bool,
      calendar_invited_project_manager: PropTypes.number,
      site: PropTypes.shape({
        id: PropTypes.number,
      }),
    }),
    project_manager: PropTypes.shape({
      id: PropTypes.number.isRequired,
      full_name: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
    }),
    follow_up_call: PropTypes.shape({
      call_number: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  action: PropTypes.string,
  onChange: PropTypes.func,
  section: PropTypes.string,
  modifyExisting: PropTypes.bool,
  preferredCalendarSiteId: PropTypes.number,
};

FollowUpDialog.defaultProps = {
  action: null,
  onChange: null,
  section: null,
  modifyExisting: false,
  preferredCalendarSiteId: undefined,
};

export default FollowUpDialog;
